/**
 * Provides Intellisense like Capabilities.
 *
 * complete.ts from @codemirror/lang-javascript version 6.1.8 AUGMENTED!!!
 *
 * Everything is the same except for localCompletionSource, where we augment
 * it to get VSCode-like intellisense capabilities for javascript.
 *
 * https://github.com/codemirror/lang-javascript/blob/6.1.8/src/complete.ts#L76
 */

// Disabling some eslint rules since the majority of the definitions here are
// taken from the page above which is not stylistically the same as our codebase
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  completeFromList,
  Completion,
  CompletionContext,
  CompletionResult,
  ifNotIn,
  insertCompletionText,
} from '@codemirror/autocomplete';
import { snippets } from '@codemirror/lang-javascript';
import { syntaxTree } from '@codemirror/language';
import { Text } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { IterMode, NodeWeakMap, SyntaxNode, SyntaxNodeRef, TreeCursor } from '@lezer/common';
import * as _ from 'lodash/fp';

import {
  BracketStringIdentifier,
  completionPath,
  Identifier,
} from 'dashboard/components-lib/CodeEditor/completionSources/javascript/utils';

const cache = new NodeWeakMap<readonly Completion[]>();

const ScopeNodes = new Set([
  'Script',
  'Block',
  'FunctionExpression',
  'FunctionDeclaration',
  'ArrowFunction',
  'MethodDeclaration',
  'ForStatement',
]);

function defID(type: string) {
  return (node: SyntaxNodeRef, def: (node: SyntaxNodeRef, type: string) => void) => {
    const id = node.node.getChild('VariableDefinition');
    if (id) def(id, type);
    return true;
  };
}

const functionContext = ['FunctionDeclaration'];

const gatherCompletions: {
  [node: string]: (
    node: SyntaxNodeRef,
    def: (node: SyntaxNodeRef, type: string) => void,
  ) => void | boolean;
} = {
  FunctionDeclaration: defID('function'),
  ClassDeclaration: defID('class'),
  ClassExpression: () => true,
  EnumDeclaration: defID('constant'),
  TypeAliasDeclaration: defID('type'),
  NamespaceDeclaration: defID('namespace'),
  VariableDefinition(node, def) {
    if (!node.matchContext(functionContext)) def(node, 'variable');
  },
  TypeDefinition(node, def) {
    def(node, 'type');
  },
  __proto__: null as any,
};

type IntellisenseDefinition = {
  [name: string]: any;
};
// State variables to assist with parsing
let intellisenseDefinition: IntellisenseDefinition = {};
let scope: string[] = [];

function buildIntellisenseDefinition(doc: Text, cursor: TreeCursor) {
  switch (cursor?.type.name) {
    case 'ObjectExpression': {
      const propertyNodes = cursor.node.getChildren('Property');
      for (const propertyNode of propertyNodes) {
        const propertyNameNode = propertyNode.getChild('PropertyDefinition');
        const stringNameNode = propertyNode.getChild('String');
        let cursor;
        if (propertyNameNode) {
          const propertyName = doc.sliceString(propertyNameNode.from, propertyNameNode.to);
          scope.push(propertyName);
          cursor = propertyNameNode.cursor();
        } else if (stringNameNode) {
          const stringName = doc.sliceString(stringNameNode.from, stringNameNode.to);
          scope.push(stringName);
          cursor = stringNameNode.cursor();
        } else {
          continue;
        }

        const valueCursor = cursor.node?.nextSibling?.nextSibling?.cursor();
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        buildIntellisenseDefinition(doc, valueCursor);
        scope.pop();
      }
      break;
    }
    case 'ArrayExpression': {
      intellisenseDefinition = _.set(scope, 'array', intellisenseDefinition);
      break;
    }
    case 'String': {
      intellisenseDefinition = _.set(scope, 'string', intellisenseDefinition);
      break;
    }
    case 'Number': {
      intellisenseDefinition = _.set(scope, 'number', intellisenseDefinition);
      break;
    }
    case 'Function': {
      intellisenseDefinition = _.set(scope, 'function', intellisenseDefinition);
      break;
    }
    case 'Block': {
      intellisenseDefinition = _.set(scope, 'function', intellisenseDefinition);
      break;
    }
    case 'ArrowFunction': {
      intellisenseDefinition = _.set(scope, 'function', intellisenseDefinition);
      break;
    }
    default: {
      break;
    }
  }
}

function getScope(context: CompletionContext, node: SyntaxNode) {
  const { doc } = context.state;
  const cached = cache.get(node);
  if (cached) return cached;

  const completions: Completion[] = [];
  let top = true;

  function def(node: SyntaxNodeRef, type: string) {
    const name = doc.sliceString(node.from, node.to);
    if (type === 'variable') {
      const cursor = node.node;
      const variableValue = cursor?.nextSibling?.nextSibling;
      scope = [];
      scope.push(name);
      intellisenseDefinition = {};
      if (variableValue) {
        buildIntellisenseDefinition(doc, variableValue.cursor(IterMode.IncludeAnonymous));
        completions.push({
          label: name,
          type,
          intellisenseDefinition,
        } as Completion);
      } else {
        completions.push({
          label: name,
          type: 'variable',
        });
      }
    } else if (type === 'function') {
      const cursor = node.node;
      const variableValue = cursor?.nextSibling?.nextSibling;
      scope = [];
      scope.push(name);
      intellisenseDefinition = {};
      if (variableValue) {
        buildIntellisenseDefinition(doc, variableValue.cursor(IterMode.IncludeAnonymous));
        completions.push({
          label: name,
          type,
          intellisenseDefinition,
        } as Completion);
      } else {
        completions.push({
          label: name,
          type: 'variable',
        });
      }
    } else {
      completions.push({
        label: name,
        type,
      });
    }
  }

  node.cursor(IterMode.IncludeAnonymous).iterate((node) => {
    if (top) {
      top = false;
    } else if (node.name) {
      const gather = gatherCompletions[node.name];
      if ((gather && gather(node, def)) || ScopeNodes.has(node.name)) return false;
    } else if (node.to - node.from > 8192) {
      // Allow caching for bigger internal nodes
      for (const c of getScope(context, node.node)) completions.push(c);
      return false;
    }
  });
  cache.set(node, completions);
  return completions;
}

export const NODE_NAME_IGNORE_LIST = [
  'TemplateString',
  'RegExp',
  'LineComment',
  'BlockComment',
  'VariableDefinition',
  'TypeDefinition',
  'Label',
  'PropertyDefinition',
  'PropertyName',
  'PrivatePropertyDefinition',
  'PrivatePropertyName',
];

interface CompletionWithIntellisenseDefinition extends Completion {
  intellisenseDefinition?: { [name: string]: any };
}

/// Completion source that looks up locally defined names in
/// JavaScript code.
///
/// NOTE (lewis): We're customized localCompletionSource to get VSCode-like
//  intellisense capabilities for javascript.
export function localIntellisenseCompletionSource(
  context: CompletionContext,
): CompletionResult | null {
  let options: Completion[] = [];
  const path = completionPath(context, NODE_NAME_IGNORE_LIST);

  const inner = syntaxTree(context.state).resolveInner(context.pos, -1);
  const outer = syntaxTree(context.state).resolveInner(context.pos, 1);
  if (!path || path.path.length === 0) {
    if (NODE_NAME_IGNORE_LIST.indexOf(inner.name) > -1) return null;

    const isWord =
      inner.name === 'VariableName' ||
      (inner.to - inner.from < 20 && Identifier.test(context.state.sliceDoc(inner.from, inner.to)));

    if (!isWord && !context.explicit) {
      return null;
    }

    for (let pos: SyntaxNode | null = inner; pos; pos = pos.parent) {
      if (ScopeNodes.has(pos.name)) options = options.concat(getScope(context, pos));
    }
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const scopedOptions = buildScopedOptions(options, path, inner, outer);

    return {
      options: scopedOptions,
      from: isWord ? inner.from : context.pos,
      validFor: Identifier,
    };
  } else {
    for (let pos: SyntaxNode | null = inner; pos; pos = pos.parent) {
      if (ScopeNodes.has(pos.name)) {
        const scopedOptions = buildScopedOptions(
          getScope(context, pos) as CompletionWithIntellisenseDefinition[],
          path,
          inner,
          outer,
        );

        options = options.concat(scopedOptions);
      }
    }

    let from = context.pos - path.name.length;
    // Check if the closing quote already exists and if so, we have to account for it
    // when we set the from position. This happens automatically if the closeBrackets()
    // extension exists.
    if (context.state.doc.sliceString(context.pos, context.pos + 1) === "'") {
      from = from + 1;
    }

    return {
      from: from,
      options: options.filter((option) => {
        // filter out private fields
        if (option.label.startsWith('__')) {
          return false;
        }

        return true;
      }),
      validFor: BracketStringIdentifier,
    };
  }
}

function enumeratePrototypeCompletions(
  obj: any,
  inner: SyntaxNode,
  outer: SyntaxNode,
): readonly Completion[] {
  const options = [];
  for (const name of Object.getOwnPropertyNames(obj)) {
    let value;
    try {
      value = obj[name];
    } catch (_) {
      continue;
    }
    options.push({
      label: name,
      type:
        typeof value == 'function'
          ? /^[A-Z]/.test(name)
            ? 'class'
            : top
              ? 'function'
              : 'method'
          : top
            ? 'variable'
            : 'property',
      onApply: onCompletionApply(name, inner, outer),
    });
  }
  return options;
}

function buildScopedOptions(
  completionOptions: CompletionWithIntellisenseDefinition[],
  path: { path: readonly string[]; name: string },
  inner: SyntaxNode,
  outer: SyntaxNode,
) {
  let options: Completion[] = [];
  for (const option of completionOptions) {
    const intellisenseDefinition = option['intellisenseDefinition'];
    if (
      typeof intellisenseDefinition === 'object' &&
      Object.keys(intellisenseDefinition).length > 0
    ) {
      let target = intellisenseDefinition;
      for (const step of path.path) {
        target = target[step];
        if (!target) break;
      }
      if (target) {
        if (typeof target == 'object') {
          options = options.concat(
            (typeof target !== 'object'
              ? []
              : Object.keys(target).map((key: string) => {
                  const value = target[key];
                  let type: string;
                  if (typeof value == 'object') {
                    type = 'object';
                  } else {
                    type = value;
                  }
                  return {
                    label: key,
                    type,
                    info: renderCompletionInfo(type, target, key),
                    apply: onCompletionApply(key, inner, outer),
                  };
                })) as Completion[],
          );
        } else if (target === 'array') {
          options = options.concat(enumeratePrototypeCompletions(Array.prototype, inner, outer));
        } else if (target === 'string') {
          options = options.concat(enumeratePrototypeCompletions(String.prototype, inner, outer));
        } else if (target === 'number') {
          options = options.concat(enumeratePrototypeCompletions(Number.prototype, inner, outer));
        } else if (target === 'boolean') {
          options = options.concat(enumeratePrototypeCompletions(Boolean.prototype, inner, outer));
        } else if (target === 'function') {
          options = options.concat(enumeratePrototypeCompletions(Function.prototype, inner, outer));
        }
      }
    }
  }

  return options;
}

function renderCompletionInfo(type: string, target: IntellisenseDefinition, key: string) {
  return (_completion: Completion) => {
    if (!['object'].includes(type)) {
      return null;
    }

    const div = document.createElement('div');
    div.style.whiteSpace = 'pre';
    div.style.fontFamily = '"DM Mono", monospace';
    div.style.fontSize = '12px';
    div.style.lineHeight = '16.8px';
    div.style.maxHeight = '160px';
    div.style.overflowY = 'scroll';

    // This is a hacky but quick way to get a nice shape for this.
    // TODO: Replace with an actual parser / formatter version of this.
    if (type === 'object') {
      const json = `${key}: ${JSON.stringify(target[key], null, 4)}`;
      const unquoted = json
        // Handle "key": "type"
        .replace(
          /['"]([^'"]+)['"]: ["']([^"']+)["']/g,
          '$1: <span class="cm-persona-completionInfo-type">$2</span>',
        )
        // Handle key: "type"
        .replace(
          /([^'"]+): ['"]([^'"]+)['"]/g,
          '$1: <span class="cm-persona-completionInfo-type">$2</span>',
        )
        // Handle object at end
        .replace(/['"]([^'"]+)['"]: {/g, '$1: {')
        // JSON.stringify a double quoted key string will result in escaped double quote
        .replace(
          /"\\"([^"]+)\\"": ["']([^"']+)["']/g,
          '"$1": <span class="cm-persona-completionInfo-type">$2</span>',
        )
        // JSON.stringify a double quoted key string ... handling for object
        .replace(/"\\"([^"]+)\\"": {/g, '"$1": {')
        // JSON.stringify will quote single quoted strings
        .replace(
          /"'([^"]+)'": ["']([^"']+)["']/g,
          '\'$1\': <span class="cm-persona-completionInfo-type">$2</span>',
        )
        // JSON.stringify will quote single quoted strings ... handling for object
        .replace(/"'([^"]+)'": {/g, "'$1': {");
      div.innerHTML = unquoted;
    }

    return div;
  };
}

function onCompletionApply(key: string, inner: SyntaxNode, outer: SyntaxNode) {
  return (view: EditorView, _completion: Completion, from: number, to: number) => {
    const singleQuoteStringKey = key[0] === "'";
    const doubleQuoteStringKey = key[0] === '"';
    const stringKey = singleQuoteStringKey || doubleQuoteStringKey;
    const label = stringKey ? key.slice(1, -1) : key;

    const quote = singleQuoteStringKey ? "'" : doubleQuoteStringKey ? '"' : null;

    if (stringKey) {
      // Handle cases where a `string` key is used to access a property
      // Examples: property['keyaccess'], property["keyaccess"]
      if (inner.name === '[') {
        if (outer.name === ']') {
          view.dispatch(insertCompletionText(view.state, `${quote}${label}${quote}`, from, to));
        } else {
          view.dispatch(insertCompletionText(view.state, `${quote}${label}${quote}]`, from, to));
        }
      } else if (inner.name === '.' || inner.name === 'PropertyName') {
        view.dispatch(insertCompletionText(view.state, `[${quote}${label}${quote}]`, from - 1, to));
      } else if (inner.name === 'String') {
        if (outer.name === 'String') {
          // There is already a closing quote (probably from closeBrackets extension)
          view.dispatch(insertCompletionText(view.state, `${quote}${label}`, from, to));
        } else {
          view.dispatch(insertCompletionText(view.state, `${quote}${label}${quote}`, from, to));
        }
      }
    } else {
      // Standard javascript property access
      // Examples: property.keyaccess
      if (inner.name === '[') {
        if (outer.name === ']') {
          view.dispatch(insertCompletionText(view.state, `.${key}`, from, to + 1));
        } else {
          view.dispatch(insertCompletionText(view.state, `.${key}`, from, to));
        }
      } else if (inner.name === '.') {
        view.dispatch(insertCompletionText(view.state, `.${key}`, from - 1, to));
      } else {
        view.dispatch(insertCompletionText(view.state, key, from, to));
      }
    }
  };
}

const keywords =
  'arguments await boolean break case catch class const continue default delete do else enum eval extends false finally for function if implements import in instanceof let new null return static super switch this throw true try typeof var void while with yield'
    .split(' ')
    .map((kw) => ({
      label: kw,
      type: 'keyword',
    }));

export const javascriptKeywordCompletionSource = ifNotIn(
  NODE_NAME_IGNORE_LIST,
  completeFromList(snippets.concat(keywords)),
);
